import * as React from 'react';
import { getLogger } from '@packages/core/logging';
import {
    IDashboardUpdates,
    DashboardActionDispatcher,
    dashboardActions,
    dashboardReducer,
    initialState,
    IDashboardState,
    IDashboard,
    baseDashboard,
} from './dashboard.state';
import { DashboardService } from './dashboard.service';

const logger = getLogger('dashboard');

export const DashboardDispatchContext = React.createContext<DashboardActionDispatcher | undefined>(undefined);

export const baseDashboardUpdates: IDashboardUpdates[] = [];

export const DashboardContext = React.createContext<IDashboardState>({
    dashboard: baseDashboard,
    dashboardUpdates: baseDashboardUpdates,
    errors: [],
    hasFetched: false,
    loading: false,
} as IDashboardState);

export const DashboardProvider: React.FC<React.PropsWithChildren<any>> = ({ children }) => {
    const [state, dispatch] = React.useReducer(dashboardReducer, initialState);
    return (
        <DashboardContext.Provider value={state}>
            <DashboardDispatchContext.Provider value={dispatch}>{children}</DashboardDispatchContext.Provider>
        </DashboardContext.Provider>
    );
};

export async function fetchDashboard({
    dashboardService,
    dashboardDispatch,
    signal,
}: {
    dashboardId?: string;
    dashboardService: DashboardService;
    signal?: AbortSignal;
    dashboardDispatch: DashboardActionDispatcher;
}) {
    dashboardDispatch(dashboardActions.isLoading());

    let response;
    try {
        response = await dashboardService.getDashboard({
            signal,
        });
    } catch (err) {
        dashboardDispatch(dashboardActions.hasLoaded());

        // A thrown error means the request never completed (network failure,
        // timeout, or abort surfacing as an exception). Record it and return.
        // NOTE: the normalized !response.success branch below intentionally still
        // rejects — fetchDashboard is awaited and handed to <AsyncPage>, which may
        // rely on that rejection. Reconciling the two paths is deferred (see ticket).
        if (err && (err as { name?: string }).name === 'AbortError') {
            return;
        }

        const message = err instanceof Error ? err.message : 'Failed to fetch dashboard';
        dashboardDispatch(dashboardActions.hasErrors({ errors: [message] }));
        return;
    }

    dashboardDispatch(dashboardActions.hasLoaded());

    if (!response.success && response.aborted) {
        return;
    } else if (!response.success) {
        dashboardDispatch(dashboardActions.hasErrors({ errors: [response.data.message] }));
        return Promise.reject(response.data);
    }

    const { dashboard } = response.data.dashboard;

    dashboardDispatch(dashboardActions.getDashboard({ dashboard }));

    return response.data;
}

export async function fetchDashboardUpdates({
    dashboardService,
    dashboardDispatch,
    signal,
}: {
    dashboardService: DashboardService;
    signal?: AbortSignal;
    dashboardDispatch: DashboardActionDispatcher;
}) {
    dashboardDispatch(dashboardActions.isLoading());

    let response;
    try {
        response = await dashboardService.getDashboardUpdate({
            signal,
        });
    } catch (err) {
        dashboardDispatch(dashboardActions.hasLoaded());

        // A thrown error (vs. a normalized { success: false } response) means the
        // request never completed — network failure, timeout, or an abort that
        // surfaced as a raw exception. Absorb it: this function is called
        // fire-and-forget, so it must never reject.
        if (err && (err as { name?: string }).name === 'AbortError') {
            return;
        }

        const message = err instanceof Error ? err.message : 'Failed to fetch dashboard updates';
        dashboardDispatch(dashboardActions.hasErrors({ errors: [message] }));
        return;
    }

    dashboardDispatch(dashboardActions.hasLoaded());

    if (!response.success && response.aborted) {
        return;
    } else if (!response.success) {
        // Normalized HTTP error. Record it in context state and return — do not
        // re-reject. Every caller of this function treats it as fire-and-forget,
        // so a rejection here has nowhere to go and becomes an unhandled rejection.
        dashboardDispatch(dashboardActions.hasErrors({ errors: [response.data.message] }));
        return;
    }

    const { dashboardUpdates } = response.data.dashboard;

    dashboardDispatch(dashboardActions.getDashboardUpdate({ dashboardUpdates }));

    return response.data;
}

export async function createOrUpdateDashboard({
    dashboardId,
    newDashboard,
    dashboardService,
    dashboardDispatch,
    signal,
}: {
    dashboardId?: string;
    newDashboard: Partial<IDashboard>;
    newDashboardUpdates?: IDashboardUpdates[];
    dashboardService: DashboardService;
    signal?: AbortSignal;
    dashboardDispatch: DashboardActionDispatcher;
}) {
    dashboardDispatch(dashboardActions.isLoading());
    try {
        if (!dashboardId) {
            const response = await dashboardService.createDashboard({
                body: {
                    dashboard: newDashboard,
                },
                signal,
            });
            dashboardDispatch(dashboardActions.hasLoaded());

            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                dashboardDispatch(dashboardActions.hasErrors({ errors: [response.data.message] }));
                return Promise.reject(response.data);
            }

            const { dashboard } = response.data.dashboard;

            dashboardDispatch(dashboardActions.createDashboard({ dashboard }));

            return response.data;
        } else {
            const response = await dashboardService.updateDashboard({
                body: {
                    dashboard: newDashboard,
                },
                signal,
            });
            dashboardDispatch(dashboardActions.hasLoaded());

            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                dashboardDispatch(dashboardActions.hasErrors({ errors: [response.data.message] }));
                return Promise.reject(response.data);
            }

            const { dashboard } = response.data.dashboard;
            const updated = dashboard.deleted ? ({} as IDashboard) : dashboard;

            dashboardDispatch(dashboardActions.updateDashboard({ dashboard: updated }));

            return response.data;
        }
    } catch (e) {
        logger.error(`Failed to ${!dashboardId ? 'create' : 'update'} the dashboard`, { dashboardId, error: e });
    }
}

export async function addOrUpdateDashboardUpdate({
    dashboardUpdate: startDashboard,
    dashboardService,
    dashboardDispatch,
    signal,
}: {
    dashboardUpdate: Partial<IDashboardUpdates>;
    dashboardService: DashboardService;
    signal?: AbortSignal;
    dashboardDispatch: DashboardActionDispatcher;
}) {
    const { dashboardUpdateId } = startDashboard;

    // UUIDs will not pass on the backend, so remove the empty strings.
    const dashboardUpdate = Object.keys(startDashboard)
        .filter((k) => startDashboard[k as keyof IDashboardUpdates])
        .reduce((a, k) => ({ ...a, [k]: startDashboard[k as keyof IDashboardUpdates] }), {}) as IDashboardUpdates;

    dashboardDispatch(dashboardActions.isLoading());
    try {
        if (!dashboardUpdateId) {
            const response = await dashboardService.createDashboardUpdate({
                dashboardUpdate,
                signal,
            });
            dashboardDispatch(dashboardActions.hasLoaded());

            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                dashboardDispatch(dashboardActions.hasErrors({ errors: [response.data.message] }));
                return Promise.reject(response.data);
            }

            const { dashboardUpdates } = response.data.dashboard;

            dashboardDispatch(dashboardActions.createDashboardUpdate({ dashboardUpdates }));

            return response.data;
        } else {
            const response = await dashboardService.updateDashboardUpdate({
                dashboardUpdate,
                signal,
            });
            dashboardDispatch(dashboardActions.hasLoaded());

            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                dashboardDispatch(dashboardActions.hasErrors({ errors: [response.data.message] }));
                return Promise.reject(response.data);
            }

            const { dashboardUpdates } = response.data.dashboard;

            dashboardDispatch(dashboardActions.updateDashboardUpdate({ dashboardUpdates }));

            return response.data;
        }
    } catch (e) {
        logger.error(`Failed to ${!dashboardUpdateId ? 'create' : 'update'} the dashboard update`, {
            dashboardUpdateId,
            error: e,
        });
    }
}

export async function deleteDashboard({
    id,
    service,
    dispatch,
    signal,
}: {
    dispatch: DashboardActionDispatcher;
    id: string;
    service: DashboardService;
    signal?: AbortSignal;
}) {
    dispatch(dashboardActions.isLoading());
    const response = await service
        .deleteDashboard({
            dashboardId: id,
            signal,
        })
        .catch((err) => {
            return err;
        });
    if (!response.success && response.aborted) {
        return;
    } else if (!response.success) {
        dispatch(dashboardActions.hasErrors({ errors: [response.data.message] }));
        return Promise.reject(response.data);
    }
    const { dashboard } = response.data.dashboard;

    dispatch(dashboardActions.updateDashboard({ dashboard }));

    return response.data;
}

export async function deleteDashboardUpdate({
    updateDashboardId,
    dashboardService,
    dashboardDispatch,
    signal,
}: {
    updateDashboardId: string;
    dashboardService: DashboardService;
    signal?: AbortSignal;
    dashboardDispatch: DashboardActionDispatcher;
}) {
    dashboardDispatch(dashboardActions.isLoading());
    const response = await dashboardService
        .deleteDashboardUpdates({
            updateDashboardId,
            signal,
        })
        .catch((err) => {
            return err;
        });
    if (!response.success && response.aborted) {
        return;
    } else if (!response.success) {
        dashboardDispatch(dashboardActions.hasErrors({ errors: [response.data.message] }));
        return Promise.reject(response.data);
    }

    dashboardDispatch(dashboardActions.deleteDashboardUpdate({ dashboardUpdateId: updateDashboardId }));

    return response.data;
}
